import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAppSelector } from "@/store";
import type { Permission } from "@/store/apis/adminApi.types";
import {
  selectHasAnyPermission,
  selectIsAal1,
  selectIsAuthenticated,
  selectSessionLost,
} from "@/store/selectors";

/**
 * AuthGuard: bloquea rutas privadas. Cuatro estados:
 *   - `sessionLost` → /sesion-expirada (se perdió sesión a media navegación).
 *   - Sin sesión y sin flag → /login (nunca inició).
 *   - aal1 (MFA pendiente)  → /mfa.
 *   - aal2 (autenticado)    → renderiza children.
 *
 * El orden importa: `sessionLost` tiene prioridad sobre `isAuthenticated`
 * porque `sessionExpired()` ya limpió los tokens — la pantalla dedicada
 * da contexto en vez de rebotar a /login sin aviso.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isAal1 = useAppSelector(selectIsAal1);
  const sessionLost = useAppSelector(selectSessionLost);
  const location = useLocation();

  if (sessionLost) return <Navigate to="/sesion-expirada" replace />;
  if (isAal1) return <Navigate to="/mfa" replace />;
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return <>{children}</>;
}

/**
 * PermissionGuard: gatea contenido por permiso. Si la usuaria no tiene
 * NINGUNO de los permisos requeridos, renderiza `fallback` (default: null).
 *
 * Uso wrapping una ruta o un fragment de UI (ej. botón "Nuevo admin"
 * solo visible con `admins.manage`).
 */
interface PermissionGuardProps {
  allow: readonly Permission[];
  fallback?: ReactNode;
  children: ReactNode;
}

export function PermissionGuard({ allow, fallback = null, children }: PermissionGuardProps) {
  const hasAny = useAppSelector(selectHasAnyPermission(allow));
  if (!hasAny) return <>{fallback}</>;
  return <>{children}</>;
}
